import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchOne as fetchCategoryConfig,
  saveOne as saveCategoryConfig
} from 'app/api/admin/browse/categoryConfigsApi';
import ConfirmUnsavedChangesModal from 'app/components/admin/browse/shared/ConfirmUnsavedChangesModal';
import FatalErrorPage from 'app/components/admin/browse/shared/FatalErrorPage';
import ErrorPage from 'app/components/ErrorPage';
import AuthConstants from 'app/constants/AuthConstants';
import { MESSAGE_ACTION_GENERIC_ERROR } from 'app/constants/admin/browse';
import { TOP_LEVEL_CATEGORY_ID, TOP_LEVEL_CATEGORY_TITLE_BY_ID } from 'app/constants/AdminBrowseConstants';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { LOCALE_CODE } from 'app/constants/language';
import { isSessionExpiredError, makeSuccessMessage } from 'app/helpers/admin/browse';
import {
  hasAdminPermissions,
  isTopLevelCategoryId,
  makeEmptyCategoryConfig,
  serializeCategoryConfig
} from 'app/helpers/admin/browse/categories';
import { readSessionStorageItem, removeSessionStorageItem, writeSessionStorageItem } from 'app/helpers/storage';
import useApiClient from 'app/hooks/useApiClient';
import useLocaleCodes from 'app/hooks/useLocalCodes';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import AdminBrowseCategoriesEditPage from './AdminBrowseCategoriesEditPage';

const AdminBrowseCategoriesEditPageContainer = ({
  basePageRoute,
  categoryConfigId,
  localeCode,
  topLevelCategoryId
}) => {
  const apiClient = useApiClient();
  const dispatch = useDispatch();
  const { isAvailableLocaleCode, mapLocaleCodeToLabel } = useLocaleCodes();

  const [categoryConfigPendingSave] = useState(() => {
    const saved = readSessionStorageItem('browsePendingCategorySave');
    if (saved) removeSessionStorageItem('browsePendingCategorySave');
    return saved;
  });

  const [state, setState] = useState({
    categoryConfig: null,
    categoryConfigWorkingCopy: null,
    hasFatalServerError: false,
    hasUnsavedChanges: false,
    isFetching: true,
    isPendingAutoSave: false
  });
  const {
    categoryConfig,
    categoryConfigWorkingCopy,
    hasFatalServerError,
    hasUnsavedChanges,
    isFetching,
    isPendingAutoSave
  } = state;

  const currentUser = useSelector(selectCurrentUser);
  const currentUsername = currentUser?.get('name');

  const currentLibrary = useSelector(selectCurrentLibrary);
  const subdomain = currentLibrary.get('domain');

  useEffect(() => {
    Promise.resolve()
      .then(() => {
        if (categoryConfigId) {
          return fetchCategoryConfig(apiClient, topLevelCategoryId, localeCode, categoryConfigId);
        }

        return makeEmptyCategoryConfig(subdomain, localeCode, topLevelCategoryId);
      })
      .then(fetchedCategoryConfig => {
        const newCategoryConfigWorkingCopy = categoryConfigPendingSave || cloneDeep(fetchedCategoryConfig);

        // Some legacy configs created before there was a back-end have a mis-match between
        // their external and internal IDs.
        // This fixes that.
        if (categoryConfigId && newCategoryConfigWorkingCopy.categoryConfigId !== categoryConfigId) {
          newCategoryConfigWorkingCopy.categoryConfigId = categoryConfigId;
        }

        setState(prevState => {
          return {
            ...prevState,
            categoryConfig: fetchedCategoryConfig,
            categoryConfigWorkingCopy: newCategoryConfigWorkingCopy,
            hasUnsavedChanges: !!categoryConfigPendingSave,
            isPendingAutoSave: !!categoryConfigPendingSave
          };
        });
      })
      .catch(() => {
        setState(prevState => ({ ...prevState, hasFatalServerError: true }));
      })
      .finally(() => {
        setState(prevState => ({ ...prevState, isFetching: false }));
      });
  }, [
    apiClient,
    categoryConfigId,
    categoryConfigPendingSave,
    currentUsername,
    dispatch,
    localeCode,
    subdomain,
    topLevelCategoryId
  ]);

  const handleSaveCategoryConfig = () => {
    setState(prevState => ({ ...prevState, isFetching: true }));

    const categoryConfigToSave = serializeCategoryConfig(categoryConfigWorkingCopy, {
      updatedAt: Date.now(),
      updatedBy: currentUsername
    });

    saveCategoryConfig(apiClient, topLevelCategoryId, localeCode, categoryConfigToSave)
      .then(savedCategoryConfig => {
        setState(prevState => {
          return {
            ...prevState,
            categoryConfig: savedCategoryConfig,
            categoryConfigWorkingCopy: cloneDeep(savedCategoryConfig),
            hasUnsavedChanges: false
          };
        });

        dispatch(makeSuccessMessage('Categories saved'));
      })
      .catch(err => {
        if (isSessionExpiredError(err)) {
          writeSessionStorageItem('browsePendingCategorySave', categoryConfigWorkingCopy);
          dispatch({ type: AuthConstants.OPEN_LOGIN_DIALOG, payload: {} });
        } else {
          dispatch(MESSAGE_ACTION_GENERIC_ERROR);
          /* eslint-disable-next-line no-console */
          console.error('Failed to save category config', err);
        }
      })
      .finally(() => {
        setState(prevState => ({ ...prevState, isFetching: false }));
      });
  };

  useEffect(() => {
    if (isPendingAutoSave && !isFetching && currentUser) {
      setState(prevState => ({ ...prevState, isPendingAutoSave: false }));
      handleSaveCategoryConfig();
    }
  }, [currentUser, isFetching, isPendingAutoSave]); // eslint-disable-line react-hooks/exhaustive-deps

  const canViewPage = hasAdminPermissions(currentUser);
  if (!canViewPage) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.FORBIDDEN} />;
  }

  const isValidLocaleCode = isAvailableLocaleCode(localeCode);
  if (!isValidLocaleCode) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  const isValidTopLevelCategoryId = isTopLevelCategoryId(topLevelCategoryId);
  if (!isValidTopLevelCategoryId) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  const backLinkLabel = 'Browse categories';
  const backLinkUrl = `${basePageRoute}/${topLevelCategoryId}`;
  const heading = `${TOP_LEVEL_CATEGORY_TITLE_BY_ID[topLevelCategoryId]} (${mapLocaleCodeToLabel(localeCode)})`;

  if (hasFatalServerError) {
    return <FatalErrorPage backLinkLabel={backLinkLabel} backLinkUrl={backLinkUrl} heading={heading} />;
  }

  const handleWorkingCopyChange = newCategoryConfigWorkingCopy => {
    const newHasUnsavedChanges = !isEqual(newCategoryConfigWorkingCopy, categoryConfig);

    setState(prevState => {
      return {
        ...prevState,
        categoryConfigWorkingCopy: newCategoryConfigWorkingCopy,
        hasUnsavedChanges: newHasUnsavedChanges
      };
    });
  };

  return (
    <>
      <ConfirmUnsavedChangesModal hasUnsavedChanges={hasUnsavedChanges} />
      <AdminBrowseCategoriesEditPage
        backLinkLabel={backLinkLabel}
        backLinkUrl={backLinkUrl}
        categoryConfig={categoryConfigWorkingCopy}
        hasUnsavedChanges={hasUnsavedChanges}
        heading={heading}
        isFetching={isFetching}
        onChange={handleWorkingCopyChange}
        onSave={handleSaveCategoryConfig}
        topLevelCategoryId={topLevelCategoryId}
      />
    </>
  );
};

AdminBrowseCategoriesEditPageContainer.propTypes = {
  basePageRoute: PropTypes.string.isRequired,
  categoryConfigId: PropTypes.string,
  localeCode: PropTypes.oneOf(Object.values(LOCALE_CODE)).isRequired,
  topLevelCategoryId: PropTypes.oneOf(Object.values(TOP_LEVEL_CATEGORY_ID)).isRequired
};

export default AdminBrowseCategoriesEditPageContainer;
